import { useEnvironmentVariables } from "#app/EnvironmentProvider.tsx";
import { HoverCardPageLink } from "#components/ui/HoverCardPageLink.tsx";
import { MemberDetails } from "#features/conversations/components/conversation-user/MemberDetails.tsx";
import { useMemberCard } from "#features/conversations/hooks/useMemberCard.ts";
import { wlAppPersonPath } from "#features/conversations/utils/wlAppPaths.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { GetMembersByIdsQueryMember } from "#types/types.ts";
import { Avatar, Flex, Link, Span, Text } from "@hopper-ui/components";
import { ClockIcon, MailIcon, ReportsToIcon } from "@hopper-ui/icons";
import type { ReactNode } from "react";
import "./MentionMemberCard.css";

interface Props {
    memberId?: string;
    // What the mention already knows, shown until the member loads and when they aren't found.
    fullName: string;
    imageUrl?: string | null;
    jobTitle?: string | null;
    // The member's id is still being looked up, so their facts are on the way.
    isLoading?: boolean;
    // The caller's own lookup found no such member; the card's lookup by id tells it itself.
    isNotFound?: boolean;
    // Links the member's wl-app page at the card's foot once they're found, holding its line while they load.
    linksToProfile?: boolean;
    onPageLinkPress?: () => void;
}

// "11:29 AM (EDT)" where the member is; nothing for a zone this browser doesn't know.
const formatLocalTime = (timeZone: string, locale: string) => {
    try {
        const now = new Date();
        const time = new Intl.DateTimeFormat(locale, { timeZone, timeStyle: "short" }).format(now);
        const zone = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: "short" })
            .formatToParts(now)
            .find(part => part.type === "timeZoneName")?.value;

        return zone ? `${time} (${zone})` : time;
    } catch {
        return undefined;
    }
};

// Every fact reads the same way: its icon, then the fact, on one line.
const Fact = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
    <Flex alignItems="center" gap="inline-sm" minWidth="core_0">
        {icon}
        {children}
    </Flex>
);

// Quiet as the facts around it. Too long for the line, the part before "@" gives way to an ellipsis first, so the domain,
// what tells a colleague from someone outside, always shows; copying it still copies the whole address.
const EmailLink = ({ email }: { email: string }) => {
    const at = email.lastIndexOf("@");

    return (
        // Named by the address whole: split in two boxes, it would read as "jane @x.com".
        <Link href={`mailto:${email}`} aria-label={email} variant="secondary" size="xs" isQuiet color="neutral-weak" display="flex" minWidth="core_0">
            <Span display="flex" minWidth="core_0" title={email}>
                <Span minWidth="core_0" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{email.slice(0, at)}</Span>
                {/* A domain wider than the line, once the part before it is gone, gives way too rather than leave the card. */}
                <Span flexShrink={0} maxWidth="100%" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{email.slice(at)}</Span>
            </Span>
        </Link>
    );
};

const MemberFacts = ({ member }: { member: GetMembersByIdsQueryMember }) => {
    const { tShared, i18n } = useSharedTranslation();
    const { reportingManager: manager, email } = member;
    const localTime = member.timeZone ? formatLocalTime(member.timeZone, i18n.language) : undefined;

    if (!manager && !localTime && !email) {
        return null;
    }

    return (
        <Flex direction="column" gap="stack-xs">
            {manager && (
                <Fact icon={<ReportsToIcon size="sm" color="neutral-weak" aria-hidden="true" flexShrink={0} />}>
                    {/* The label keeps its width, as "Relève de" needs; a long name gives way with an ellipsis. */}
                    <Text fontSize="body-xs" color="neutral-weak" flexShrink={0} whiteSpace="nowrap">{tShared("personProfile.reportsToLabel")}</Text>
                    {/* Avatar and name as close as in a mention chip. */}
                    <Flex alignItems="center" gap="inline-xs" minWidth="core_0">
                        {/* The name beside it already says who it is. */}
                        <Span display="inline-flex" flexShrink={0} aria-hidden="true">
                            <Avatar data-private name={manager.fullName} src={manager.imageUrl ?? undefined} size="xs" />
                        </Span>
                        <Text fontSize="body-xs" color="neutral" minWidth="core_0" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={manager.fullName}>{manager.fullName}</Text>
                    </Flex>
                </Fact>
            )}
            {localTime && (
                <Fact icon={<ClockIcon size="sm" color="neutral-weak" aria-hidden="true" flexShrink={0} />}>
                    <Text fontSize="body-xs" color="neutral-weak">{localTime}</Text>
                </Fact>
            )}
            {email && (
                <Fact icon={<MailIcon size="sm" color="neutral-weak" aria-hidden="true" flexShrink={0} />}>
                    <EmailLink email={email} />
                </Fact>
            )}
        </Flex>
    );
};

// Holds the facts' three lines (manager, local time, email) while the member loads, so the card never grows once open;
// and the page link's line under them, where the card will link one.
const MemberFactsPlaceholder = ({ hasPageLink = false }: { hasPageLink?: boolean }) => {
    const facts = (
        <div className="wai-member-facts-placeholder" aria-hidden="true" data-testid="member-facts-placeholder">
            <span className="wai-member-facts-placeholder__line wai-member-facts-placeholder__line--long" />
            <span className="wai-member-facts-placeholder__line" />
            <span className="wai-member-facts-placeholder__line wai-member-facts-placeholder__line--long" />
        </div>
    );

    return hasPageLink
        ? (
            <>
                {facts}
                <span className="wai-member-facts-placeholder__line wai-member-facts-placeholder__line--link" aria-hidden="true" data-testid="page-link-placeholder" />
            </>
        )
        : facts;
};

// Said only when the lookup answered that there is no such member; a failed lookup keeps the name alone.
const MemberNotFound = () => {
    const { tShared } = useSharedTranslation();

    return <Text fontSize="body-xs" color="neutral-weak">{tShared("personProfile.notFoundInOrganization")}</Text>;
};

const LoadedMemberCard = ({ memberId, linksToProfile = false, onPageLinkPress, ...known }: Required<Pick<Props, "memberId">> & Omit<Props, "memberId" | "isLoading" | "isNotFound">) => {
    const { tShared } = useSharedTranslation();
    const { wlAppBaseUrl } = useEnvironmentVariables();
    const { data: member, isLoading } = useMemberCard(memberId);

    if (!member) {
        return (
            <MemberDetails {...known} details={isLoading && <MemberFactsPlaceholder hasPageLink={linksToProfile} />}>
                {member === null && <MemberNotFound />}
            </MemberDetails>
        );
    }

    const role = [member.jobTitle, member.department].filter(Boolean).join(" · ");

    const details = (
        <>
            <MemberFacts member={member} />
            {linksToProfile && <HoverCardPageLink href={`${wlAppBaseUrl}${wlAppPersonPath(member.id)}`} onPress={onPageLinkPress}>{tShared("viewProfile")}</HoverCardPageLink>}
        </>
    );

    return <MemberDetails fullName={member.fullName} imageUrl={member.imageUrl} jobTitle={role || undefined} details={details} />;
};

// A mentioned member's card, as their profile page's card shows them: role and department, manager, local time, email.
export const MentionMemberCard = ({ memberId, isLoading = false, isNotFound = false, linksToProfile = false, onPageLinkPress, ...known }: Props) => (memberId
    ? <LoadedMemberCard memberId={memberId} linksToProfile={linksToProfile} onPageLinkPress={onPageLinkPress} {...known} />
    : (
        <MemberDetails {...known} details={isLoading && <MemberFactsPlaceholder hasPageLink={linksToProfile} />}>
            {isNotFound && <MemberNotFound />}
        </MemberDetails>
    ));
